import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { pb } from '../lib/pocketbase.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { applyFilterRegel } from '../lib/praxisFilter.js';
import SelektionFormModal from '../components/SelektionFormModal.jsx';

function userLabel(user) {
  if (!user) return 'Unbekannt';
  const name = `${user.vorname ?? ''} ${user.nachname ?? ''}`.trim();
  return name || user.email || 'Unbekannt';
}

function kuerze(text, max = 160) {
  if (!text) return '';
  // HTML aus der editor-Beschreibung roh anzeigen wir nicht — Tags
  // entfernen und in eine Plain-Text-Zusammenfassung kürzen.
  const plain = String(text)
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (plain.length <= max) return plain;
  return plain.slice(0, max - 1).trimEnd() + '…';
}

function SelektionCard({ selektion, ersteller, offen, erledigt, isMine }) {
  const beschreibung = kuerze(selektion.beschreibung);
  const gesamt = offen + erledigt;
  return (
    <Link
      to={`/selektionen/${selektion.id}`}
      className="card flex flex-col gap-3 transition hover:border-primary-300 hover:shadow-md"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">
            {selektion.name}
          </h3>
          <p className="mt-0.5 text-xs text-slate-500">
            {isMine ? 'Erstellt von dir' : `Erstellt von ${userLabel(ersteller)}`}
          </p>
        </div>
      </div>
      {beschreibung && (
        <p className="text-sm text-slate-600 line-clamp-3">{beschreibung}</p>
      )}
      <div className="mt-auto flex flex-wrap items-center gap-3 border-t border-slate-100 pt-3 text-sm">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-2 w-2 rounded-full bg-primary-500" />
          <span className="font-semibold text-slate-900">{offen}</span>
          <span className="text-slate-500">offen</span>
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
          <span className="font-semibold text-slate-900">{erledigt}</span>
          <span className="text-slate-500">erledigt</span>
        </span>
        <span className="ml-auto text-xs text-slate-400">
          {gesamt} Praxen gesamt
        </span>
      </div>
    </Link>
  );
}

function Gruppe({ titel, selektionen, statsBySelektion, currentUserId }) {
  if (selektionen.length === 0) return null;
  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
        {titel}
      </h2>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {selektionen.map((s) => {
          const stats = statsBySelektion.get(s.id) ?? { offen: 0, erledigt: 0 };
          return (
            <SelektionCard
              key={s.id}
              selektion={s}
              ersteller={s.expand?.erstellt_von}
              offen={stats.offen}
              erledigt={stats.erledigt}
              isMine={s.erstellt_von === currentUserId}
            />
          );
        })}
      </div>
    </section>
  );
}

export default function SelektionenListPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selektionen, setSelektionen] = useState([]);
  const [praxen, setPraxen] = useState([]);
  const [erledigtEintraege, setErledigtEintraege] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [selektionenListe, praxenListe, erledigtListe] = await Promise.all([
        pb.collection('selektionen').getFullList({
          filter: 'aktiv = true',
          sort: '-created',
          expand: 'erstellt_von',
        }),
        // Nur die Felder, die für das Filter-Matching gebraucht werden.
        // Spart Bandbreite, falls die Praxenliste mal groß wird.
        pb.collection('praxen').getFullList({
          fields: 'id,status,praxis_art,versorgungsform,verbund,typ,fachgebiet_neu,plz',
        }),
        pb.collection('selektion_erledigt').getFullList({
          fields: 'id,selektion,praxis',
        }),
      ]);
      setSelektionen(selektionenListe);
      setPraxen(praxenListe);
      setErledigtEintraege(erledigtListe);
    } catch (err) {
      setError(err?.message || 'Selektionen konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Pro Selektion: wie viele Praxen matchen die Filterregel? Davon
  // ziehen wir die erledigten ab — auch wenn ein erledigter Eintrag aus
  // einer Praxis stammt, die heute nicht mehr matchen würde, zählen
  // wir ihn als "erledigt", weil er ja real abgehakt wurde.
  const statsBySelektion = useMemo(() => {
    const erledigtBySelektion = new Map();
    for (const eintrag of erledigtEintraege) {
      const set = erledigtBySelektion.get(eintrag.selektion) ?? new Set();
      set.add(eintrag.praxis);
      erledigtBySelektion.set(eintrag.selektion, set);
    }
    const map = new Map();
    for (const s of selektionen) {
      const matches = applyFilterRegel(praxen, s.filter_regel);
      const erledigtPraxisIds = erledigtBySelektion.get(s.id) ?? new Set();
      // "offen" = matched UND nicht erledigt; "erledigt" = Anzahl der Marks
      // (unabhängig davon, ob die Praxis heute noch matcht).
      let offen = 0;
      for (const p of matches) {
        if (!erledigtPraxisIds.has(p.id)) offen += 1;
      }
      map.set(s.id, { offen, erledigt: erledigtPraxisIds.size });
    }
    return map;
  }, [selektionen, praxen, erledigtEintraege]);

  const { meine, geteilte } = useMemo(() => {
    const meine = [];
    const geteilte = [];
    for (const s of selektionen) {
      if (s.erstellt_von === user?.id) meine.push(s);
      else geteilte.push(s);
    }
    return { meine, geteilte };
  }, [selektionen, user?.id]);

  function handleSaved(saved) {
    // Direkt auf die frisch angelegte Selektion springen — der User will in
    // 99% der Fälle gleich die gefilterte Liste sehen und nicht erst auf
    // die Übersicht zurück.
    if (saved?.id) {
      navigate(`/selektionen/${saved.id}`);
    } else {
      load();
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Selektionen</h1>
          <p className="mt-1 text-sm text-slate-600">
            Kuratierte Listen von Praxen zum Abtelefonieren. Selektionen
            aktualisieren sich automatisch, wenn neue Praxen zum Filter passen.
          </p>
        </div>
        <button
          type="button"
          className="btn-primary"
          onClick={() => setModalOpen(true)}
        >
          + Neue Selektion
        </button>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 ring-1 ring-red-200">
          {error}
        </div>
      )}

      {loading ? (
        <div className="card text-sm text-slate-500">Lade …</div>
      ) : selektionen.length === 0 ? (
        <div className="card text-center">
          <p className="text-sm text-slate-600">
            Noch keine Selektionen angelegt.
          </p>
          <button
            type="button"
            className="btn-primary mt-3"
            onClick={() => setModalOpen(true)}
          >
            Erste Selektion anlegen
          </button>
        </div>
      ) : (
        <div className="space-y-8">
          <Gruppe
            titel="Meine Selektionen"
            selektionen={meine}
            statsBySelektion={statsBySelektion}
            currentUserId={user?.id}
          />
          <Gruppe
            titel="Geteilte Selektionen"
            selektionen={geteilte}
            statsBySelektion={statsBySelektion}
            currentUserId={user?.id}
          />
          {meine.length === 0 && (
            <div className="rounded-md bg-slate-50 p-3 text-xs text-slate-500">
              Du hast selbst noch keine Selektion angelegt.{' '}
              <button
                type="button"
                onClick={() => setModalOpen(true)}
                className="font-medium text-primary-600 hover:text-primary-800"
              >
                Jetzt anlegen
              </button>
            </div>
          )}
        </div>
      )}

      <SelektionFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={handleSaved}
      />
    </div>
  );
}
